import { mdiFilterOutline } from '@mdi/js';
import _ from 'lodash';
import React, { useState, useEffect } from 'react';
import { PropsValue } from 'react-select';
import { BaseSelect, ISelectOption } from '../../../base';
import { FormField } from '../../../form-field';
import { Popover } from '../../../base/popover';
import { FilterValue } from '../filters/filter-item';
import { 
    FilterMethod, 
    IColumnFilterConfig,
    ICustomFilter,
    IFilterSchema
} from '../filters/filters.models';
import { getFilterMethods, isEmptyValueMethod } from '../filters/filters.service';
import * as S from './column-filter.styles';

export interface IColumnFilterProps {
    columnId: string;
    columnLabel: string;
    filterConfig: IColumnFilterConfig;
    currentFilter?: ICustomFilter;
    onApply: (filter: ICustomFilter | undefined) => void;
}

export const ColumnFilter: React.FC<IColumnFilterProps> = ({
    columnId,
    columnLabel,
    filterConfig,
    currentFilter,
    onApply,
}) => {
    const [filterMethod, setFilterMethod] = useState<FilterMethod | undefined>(
        currentFilter?.filterMethod || filterConfig.defaultMethod || FilterMethod.LK
    );
    const [filterValue, setFilterValue] = useState<any>(currentFilter?.value);
    const [isOpen, setIsOpen] = useState(false);
    
    // Track previous currentFilter to detect actual changes
    const prevCurrentFilterRef = React.useRef<ICustomFilter | undefined>(currentFilter);

    useEffect(() => {
        const filterChanged = !_.isEqual(prevCurrentFilterRef.current, currentFilter);
        
        if (filterChanged) {
            if (currentFilter) {
                setFilterMethod(currentFilter.filterMethod);
                setFilterValue(currentFilter.value);
            } else {
                // Only reset if we had a filter before and now it's cleared
                if (prevCurrentFilterRef.current) {
                    setFilterMethod(filterConfig.defaultMethod || FilterMethod.LK);
                    setFilterValue(undefined);
                }
            }
            prevCurrentFilterRef.current = currentFilter;
        }
    }, [currentFilter, filterConfig.defaultMethod]);

    const filterType = filterConfig.filterType === 'Custom' ? 'String' : filterConfig.filterType;
    const filterMethods = filterConfig.filterType === 'Custom' ? [] : getFilterMethods(filterType);
    const emptyValueMethod = filterMethod && isEmptyValueMethod(filterMethod);
    
    const selectedMethod = _.find(filterMethods, { value: filterMethod });

    const handleMethodChange = (option: PropsValue<ISelectOption>) => {
        if (!option) {
            return;
        }
        const selectedOption = option as ISelectOption;
        setFilterMethod(selectedOption.value as FilterMethod);
    };

    const handleValueChange = (value: any) => {
        setFilterValue(value);
    };

    const handleApply = () => {
        if (!filterMethod) {
            return;
        }

        // Don't require value for empty/not empty methods
        if (!emptyValueMethod && (filterValue === undefined || filterValue === '' || filterValue === null)) {
            return;
        }

        const filter: ICustomFilter = {
            filterId: filterConfig.filterId || columnId,
            filterMethod,
            filterType: filterType,
            value: emptyValueMethod ? undefined : filterValue,
            label: columnLabel,
        } as ICustomFilter;

        onApply(filter);
        setIsOpen(false);
    };

    const handleClear = () => {
        setFilterMethod(filterConfig.defaultMethod || FilterMethod.LK);
        setFilterValue(undefined);
        onApply(undefined);
        setIsOpen(false);
    };

    const hasActiveFilter = !!currentFilter;

    const renderButton = () => (
        <S.FilterIconButton className={hasActiveFilter ? 'active' : ''}>
            <S.FilterIcon path={mdiFilterOutline} size={0.6} />
        </S.FilterIconButton>
    );

    const renderFilterContent = () => {
        if (filterConfig.filterType === 'Custom') {
            const CustomComponent = filterConfig.component;
            return (
                <S.FilterPopoverContent onClick={(e) => e.stopPropagation()}>
                    <S.FilterLabel>{filterConfig.label || columnLabel}</S.FilterLabel>
                    <CustomComponent
                        value={filterValue}
                        onChange={handleValueChange}
                        filterMethod={filterMethod}
                        onFilterMethodChange={setFilterMethod}
                    />
                    <S.FilterActions>
                        <S.ClearButton variant="outlined" onClick={handleClear}>
                            Clear
                        </S.ClearButton>
                        <S.ApplyButton variant="primary" onClick={handleApply}>
                            Apply
                        </S.ApplyButton>
                    </S.FilterActions>
                </S.FilterPopoverContent>
            );
        }

        // Create a filter schema for built-in types
        const filterSchema: IFilterSchema = {
            filterId: columnId,
            label: columnLabel,
            placeholder: filterConfig.placeholder,
            filterType: filterType,
            columnId,
            ...(filterConfig.filterType === 'Select' && filterConfig.options ? { options: filterConfig.options } : {}),
            ...(filterConfig.filterType === 'AsyncSelect' && filterConfig.loadOptions ? { loadOptions: filterConfig.loadOptions } : {}),
        } as IFilterSchema;

        return (
            <S.FilterPopoverContent onClick={(e) => e.stopPropagation()}>
                <S.FilterLabel>{filterConfig.label || columnLabel}</S.FilterLabel>
                {filterMethods.length > 0 && (
                    <S.FilterMethodRow>
                        <FormField>
                            <BaseSelect
                                menuPosition="fixed"
                                onChange={handleMethodChange}
                                options={filterMethods}
                                value={selectedMethod}
                                placeholder="Condition"
                            />
                        </FormField>
                    </S.FilterMethodRow>
                )}
                {!emptyValueMethod && (
                    <S.FilterValueRow>
                        <FormField>
                            <FilterValue
                                index={0}
                                filterValue={filterValue}
                                filterSchema={filterSchema}
                                onChange={handleValueChange}
                            />
                        </FormField>
                    </S.FilterValueRow>
                )}
                <S.FilterActions>
                    <S.ClearButton variant="outlined" onClick={handleClear}>
                        Clear
                    </S.ClearButton>
                    <S.ApplyButton variant="primary" onClick={handleApply}>
                        Apply
                    </S.ApplyButton>
                </S.FilterActions>
            </S.FilterPopoverContent>
        );
    };

    return (
        <Popover
            renderButton={renderButton}
            open={isOpen}
            onOpenChange={setIsOpen}
            verticalAlign="bottom"
            align="left"
        >
            {renderFilterContent()}
        </Popover>
    );
};

